import { Link } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { SEO } from '../components/common/SEO';
import './LegalPages.css';

/**
 * Terms and Conditions Page
 * Legal terms for using Rongeka Africa school management system
 */
export const TermsAndConditions: React.FC = () => {
  return (
    <div className="legal-page-container">
      <SEO
        title="Terms and Conditions | Rongeka Africa"
        description="The terms and conditions for using Rongeka Africa, the school management system for Zimbabwean schools by Rongeka Technologies."
        url="https://rongeka.africa/terms"
      />
      <div className="legal-page-content">
        <Link to="/" className="legal-back-link">
          <ArrowLeftOutlined /> Back to Home
        </Link>

        <h1>Terms and Conditions</h1>
        <p className="legal-updated">Last Updated: December 16, 2025</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using Rongeka Africa ("the Platform"), you accept and agree to be bound by the terms 
            and provision of this agreement. If you do not agree to these Terms and Conditions, please do not use 
            the Platform.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            Rongeka Africa provides a comprehensive school management System designed for Zimbabwean schools, including
            but not limited to:
          </p>
          <ul>
            <li>Student records management</li>
            <li>QR code-based attendance tracking</li>
            <li>Fee and payment processing</li>
            <li>Bulk SMS and WhatsApp messaging to parents</li>
            <li>Report generation and analytics</li>
            <li>Student ID card creation</li>
            <li>Teacher and assessment management</li>
          </ul>
        </section>

        <section>
          <h2>3. User Accounts and Registration</h2>
          <p>
            3.1. Schools must register with accurate and complete information.<br />
            3.2. Each school is responsible for maintaining the confidentiality of their account credentials.<br />
            3.3. Schools are responsible for all activities that occur under their account.<br />
            3.4. You must notify us immediately of any unauthorized use of your account.
          </p>
        </section>

        <section>
          <h2>4. Free Trial and Subscription</h2>
          <p>
            4.1. New schools receive a 30-day free trial period.<br />
            4.2. After the trial period, continued use requires a paid subscription.<br />
            4.3. Subscription fees and payment terms will be communicated separately.<br />
            4.4. We reserve the right to modify subscription fees with 30 days' notice.
          </p>
        </section>

        <section>
          <h2>5. Data Ownership and Usage</h2>
          <p>
            5.1. Schools retain ownership of all data entered into the Platform.<br />
            5.2. We do not sell, rent, or share school data with third parties for marketing purposes.<br />
            5.3. We may use aggregated, anonymized data for service improvement and analytics.<br />
            5.4. Schools are responsible for ensuring they have proper consent to collect and process student data.
          </p>
        </section>

        <section>
          <h2>6. Data Protection and Privacy</h2>
          <p>
            6.1. We comply with Zimbabwe's Data Protection Act and applicable data protection regulations.<br />
            6.2. Personal data is processed in accordance with our Privacy Policy.<br />
            6.3. We implement appropriate technical and organizational measures to protect data.<br />
            6.4. Schools must comply with all applicable data protection laws when using the Platform.
          </p>
        </section>

        <section>
          <h2>7. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Platform for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to the Platform or related systems</li>
            <li>Interfere with or disrupt the Platform's operation</li>
            <li>Upload malicious code, viruses, or harmful content</li>
            <li>Impersonate another person or entity</li>
            <li>Violate any applicable laws or regulations</li>
          </ul>
        </section>

        <section>
          <h2>8. Intellectual Property</h2>
          <p>
            8.1. The Platform, including its design, features, and content, is owned by Rongeka Africa.<br />
            8.2. You may not copy, modify, distribute, or reverse engineer any part of the Platform.<br />
            8.3. School logos and branding uploaded by schools remain the property of those schools.
          </p>
        </section>

        <section>
          <h2>9. Service Availability</h2>
          <p>
            9.1. We strive to maintain 99.9% uptime but do not guarantee uninterrupted service.<br />
            9.2. We may perform scheduled maintenance with advance notice.<br />
            9.3. We are not liable for service interruptions beyond our reasonable control.
          </p>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <p>
            10.1. The Platform is provided "as is" without warranties of any kind.<br />
            10.2. We are not liable for any indirect, incidental, or consequential damages.<br />
            10.3. Our total liability shall not exceed the amount paid by the school in the past 12 months.
          </p>
        </section>

        <section>
          <h2>11. Termination</h2>
          <p>
            11.1. Schools may terminate their account at any time.<br />
            11.2. We may suspend or terminate accounts for violation of these terms.<br />
            11.3. Upon termination, schools may export their data within 30 days.<br />
            11.4. We reserve the right to delete data after account termination.
          </p>
        </section>

        <section>
          <h2>12. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms and Conditions at any time. Changes will be effective 
            immediately upon posting. Continued use of the Platform constitutes acceptance of modified terms.
          </p>
        </section>

        <section>
          <h2>13. Governing Law</h2>
          <p>
            These Terms and Conditions are governed by the laws of Zimbabwe. Any disputes shall be resolved 
            in the courts of Zimbabwe.
          </p>
        </section>

        <section>
          <h2>14. Contact Information</h2>
          <p>
            For questions about these Terms and Conditions, please contact us at:<br />
            Email: support@rongeka.africa
          </p>
        </section>

        <div className="legal-footer">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span>•</span>
          <Link to="/">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};
